import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';

import type { TableDataFileItem } from '../../api/types.js';
import { getDataFileStatusContent } from '../../utils/dataFileUtils.jsx';
import { CheckboxWithLabel } from '../atoms/CheckboxWithLabel.jsx';

type Props = {
  dataFile: TableDataFileItem;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
};

export const ImportDeleteDialog = ({
  dataFile,
  isSubmitting,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation('@myn/import-hub');
  const { label: statusLabel, editable } = getDataFileStatusContent(
    t,
    dataFile,
  );

  const [isDeleteConsentGiven, setIsDeleteConsentGiven] = useState(editable);

  const handleDeleteConsentChanged = (checked: boolean) => {
    setIsDeleteConsentGiven(checked);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader>
            <DialogTitle className="flex flex-row items-center justify-center gap-2">
              <WarningAltFilled className="size-5 fill-error-main" />
              <p>
                {t('Delete this file in {{statusLabel}} status?', {
                  statusLabel,
                })}
              </p>
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex-col">
            <DialogDescription>
              {editable
                ? t(
                    'If you proceed, all progresses you made during the import steps will be deleted.',
                  )
                : t(
                    'If you proceed, you will permanently remove the selected file from our system. All data imported from this file will be permanently deleted from the Reporting.',
                  )}
            </DialogDescription>
            {!editable && (
              <div className="my-4">
                <CheckboxWithLabel
                  label={t(
                    'I understand I will not be able to recover the data',
                  )}
                  checked={isDeleteConsentGiven}
                  onCheckedChange={handleDeleteConsentChanged}
                />
              </div>
            )}
          </DialogContent>
          <DialogFooter>
            <Button size="large" variant="text" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              color="error"
              onClick={onSubmit}
              disabled={isSubmitting || !isDeleteConsentGiven}
            >
              {t('Yes, delete')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
